/**
 * MedicationReminderModal
 *
 * Bottom-sheet modal for creating or editing a medication reminder.
 * Supports interval-based and fixed-time scheduling, date ranges, and multi-child.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Check, Plus, Trash2, Clock } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { getBabiesFromPregnancy } from '@/lib/babyUtils';
import type {
  MedicationReminder,
  CreateMedicationReminder,
  UpdateMedicationReminder,
  ScheduleMode,
} from '@/services/medicationReminderService';

interface MedicationReminderModalProps {
  pregnancyId: string;
  existingReminder?: MedicationReminder;
  onSave: (
    data: CreateMedicationReminder | { id: string; updates: UpdateMedicationReminder }
  ) => Promise<boolean>;
  onClose: () => void;
}

const INTERVAL_OPTIONS = [4, 6, 8, 12, 24, 48];

export function MedicationReminderModal({
  pregnancyId,
  existingReminder,
  onSave,
  onClose,
}: MedicationReminderModalProps) {
  const { t } = useTranslation('babyCare');
  const { lastCompletedPregnancy } = usePregnancyStore();
  const babies = getBabiesFromPregnancy(lastCompletedPregnancy);
  const hasMultipleBabies = babies.length > 1;

  const isEditing = !!existingReminder;

  // Form state
  const [medicationName, setMedicationName] = useState(existingReminder?.medication_name || '');
  const [medicationDose, setMedicationDose] = useState(existingReminder?.medication_dose || '');
  const [selectedBabyIndex, setSelectedBabyIndex] = useState(existingReminder?.baby_index ?? 0);
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>(
    existingReminder?.schedule_mode || 'interval'
  );
  const [intervalHours, setIntervalHours] = useState<number>(
    existingReminder?.interval_hours || 6
  );
  const [fixedTimes, setFixedTimes] = useState<string[]>(() => {
    const raw = existingReminder?.fixed_times;
    if (raw) {
      if (Array.isArray(raw)) return raw;
      // Handle double-stringified JSONB (stored as string instead of array)
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed;
        } catch { /* fall through to default */ }
      }
    }
    return ['08:00', '20:00'];
  });
  const [startDate, setStartDate] = useState(
    existingReminder?.start_date?.split('T')[0] || new Date().toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    existingReminder?.end_date?.split('T')[0] || ''
  );
  const [hasEndDate, setHasEndDate] = useState(!!existingReminder?.end_date);
  const [notes, setNotes] = useState(existingReminder?.notes || '');

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleAddFixedTime = () => {
    setFixedTimes((prev) => [...prev, '12:00']);
  };

  const handleRemoveFixedTime = (index: number) => {
    setFixedTimes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFixedTimeChange = (index: number, value: string) => {
    setFixedTimes((prev) => prev.map((t, i) => (i === index ? value : t)));
  };

  const handleSubmit = async () => {
    if (!medicationName.trim()) return;

    setIsSaving(true);

    let success: boolean;

    if (isEditing && existingReminder) {
      const updates: UpdateMedicationReminder = {
        medication_name: medicationName.trim(),
        medication_dose: medicationDose.trim() || null,
        schedule_mode: scheduleMode,
        interval_hours: scheduleMode === 'interval' ? intervalHours : null,
        fixed_times: scheduleMode === 'fixed_times' ? fixedTimes.sort() : [],
        start_date: startDate,
        end_date: hasEndDate && endDate ? endDate : null,
        notes: notes.trim() || null,
      };
      success = await onSave({ id: existingReminder.id, updates });
    } else {
      const data: CreateMedicationReminder = {
        pregnancy_id: pregnancyId,
        baby_index: selectedBabyIndex,
        medication_name: medicationName.trim(),
        medication_dose: medicationDose.trim() || undefined,
        schedule_mode: scheduleMode,
        interval_hours: scheduleMode === 'interval' ? intervalHours : undefined,
        fixed_times: scheduleMode === 'fixed_times' ? fixedTimes.sort() : undefined,
        start_date: startDate,
        end_date: hasEndDate && endDate ? endDate : null,
        notes: notes.trim() || undefined,
      };
      success = await onSave(data);
    }

    setIsSaving(false);

    if (success) {
      setShowSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1000);
    }
  };

  const isFormValid =
    medicationName.trim() !== '' &&
    (scheduleMode === 'interval' || fixedTimes.length > 0);

  const modalContent = (
    <>
      {/* Backdrop */}
      <div className="bottom-sheet-backdrop" onClick={onClose} />

      {/* Modal Container */}
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <h2>
              {isEditing
                ? t('medicationPlan.editReminder')
                : t('medicationPlan.newReminder')}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content">
            {showSuccess ? (
              <div className="flex flex-col items-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center mb-4">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <p className="text-white text-lg font-medium">
                  {t('medicationPlan.saved')}
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Baby Selector (multi-child) */}
                {hasMultipleBabies && !isEditing && (
                  <div>
                    <label className="block text-white/70 text-sm mb-2">
                      {t('entryModal.babySelect')}
                    </label>
                    <div className="flex gap-2">
                      {babies.map((baby) => (
                        <button
                          key={baby.index}
                          onClick={() => setSelectedBabyIndex(baby.index)}
                          className={`flex-1 py-3 rounded-[10px] font-medium transition-colors ${
                            selectedBabyIndex === baby.index
                              ? 'bg-pp-orange text-white'
                              : 'bg-white/10 text-white/70'
                          }`}
                        >
                          {baby.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Medication Name */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    {t('medicationPlan.medicationName')}
                  </label>
                  <input
                    type="text"
                    value={medicationName}
                    onChange={(e) => setMedicationName(e.target.value)}
                    placeholder={t('entryModal.medicationPlaceholder')}
                    className="w-full py-3 px-4 bg-white/10 rounded-[10px] text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-pp-orange"
                  />
                </div>

                {/* Dosage */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    {t('medicationPlan.dosage')}
                  </label>
                  <input
                    type="text"
                    value={medicationDose}
                    onChange={(e) => setMedicationDose(e.target.value)}
                    placeholder={t('entryModal.dosagePlaceholder')}
                    className="w-full py-3 px-4 bg-white/10 rounded-[10px] text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-pp-orange"
                  />
                </div>

                {/* Schedule Mode */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    {t('medicationPlan.scheduleMode')}
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setScheduleMode('interval')}
                      className={`flex-1 py-3 rounded-[10px] font-medium transition-colors ${
                        scheduleMode === 'interval'
                          ? 'bg-pp-orange text-white'
                          : 'bg-white/10 text-white/70'
                      }`}
                    >
                      {t('medicationPlan.intervalMode')}
                    </button>
                    <button
                      onClick={() => setScheduleMode('fixed_times')}
                      className={`flex-1 py-3 rounded-[10px] font-medium transition-colors ${
                        scheduleMode === 'fixed_times'
                          ? 'bg-pp-orange text-white'
                          : 'bg-white/10 text-white/70'
                      }`}
                    >
                      {t('medicationPlan.fixedTimesMode')}
                    </button>
                  </div>
                </div>

                {/* Interval Selection */}
                {scheduleMode === 'interval' && (
                  <div>
                    <label className="block text-white/70 text-sm mb-2">
                      {t('medicationPlan.intervalLabel')}
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {INTERVAL_OPTIONS.map((hours) => (
                        <button
                          key={hours}
                          onClick={() => setIntervalHours(hours)}
                          className={`px-4 py-3 rounded-[10px] font-medium transition-colors ${
                            intervalHours === hours
                              ? 'bg-pp-orange text-white'
                              : 'bg-white/10 text-white/70'
                          }`}
                        >
                          {t('medicationPlan.intervalHours', { hours })}
                        </button>
                      ))}
                    </div>
                    <p className="text-white/50 text-xs mt-2">
                      {t('medicationPlan.intervalHint', { hours: intervalHours })}
                    </p>
                  </div>
                )}

                {/* Fixed Times */}
                {scheduleMode === 'fixed_times' && (
                  <div>
                    <label className="block text-white/70 text-sm mb-2">
                      {t('medicationPlan.fixedTimesLabel')}
                    </label>
                    <div className="space-y-2">
                      {fixedTimes.map((time, index) => (
                        <div key={index} className="flex gap-2 items-center">
                          <div className="flex-1 relative">
                            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                            <input
                              type="time"
                              value={time}
                              onChange={(e) => handleFixedTimeChange(index, e.target.value)}
                              className="w-full py-3 pl-10 pr-4 bg-white/10 rounded-[10px] text-white outline-none focus:ring-2 focus:ring-pp-orange [color-scheme:dark]"
                            />
                          </div>
                          {fixedTimes.length > 1 && (
                            <button
                              onClick={() => handleRemoveFixedTime(index)}
                              className="w-10 h-10 rounded-[10px] bg-white/10 flex items-center justify-center text-white/50 hover:text-red-400 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {fixedTimes.length < 6 && (
                      <button
                        onClick={handleAddFixedTime}
                        className="mt-2 flex items-center gap-2 text-pp-orange text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        {t('medicationPlan.addTime')}
                      </button>
                    )}
                  </div>
                )}

                {/* Date Range */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    {t('medicationPlan.dateRange')}
                  </label>
                  <div className="space-y-3">
                    <div>
                      <span className="text-white/50 text-xs mb-1 block">
                        {t('medicationPlan.startDate')}
                      </span>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full py-3 px-4 bg-white/10 rounded-[10px] text-white outline-none focus:ring-2 focus:ring-pp-orange [color-scheme:dark]"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setHasEndDate(!hasEndDate)}
                        className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                          hasEndDate
                            ? 'bg-pp-orange border-pp-orange'
                            : 'border-white/30 bg-transparent'
                        }`}
                      >
                        {hasEndDate && <Check className="w-4 h-4 text-white" />}
                      </button>
                      <span className="text-white/70 text-sm">
                        {t('medicationPlan.hasEndDate')}
                      </span>
                    </div>
                    {hasEndDate && (
                      <div>
                        <span className="text-white/50 text-xs mb-1 block">
                          {t('medicationPlan.endDate')}
                        </span>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          min={startDate}
                          className="w-full py-3 px-4 bg-white/10 rounded-[10px] text-white outline-none focus:ring-2 focus:ring-pp-orange [color-scheme:dark]"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    {t('entryModal.notesOptional')}
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t('entryModal.notesPlaceholder')}
                    rows={2}
                    className="w-full py-3 px-4 bg-white/10 rounded-[10px] text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-pp-orange resize-none"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {!showSuccess && (
            <div className="bottom-sheet-footer">
              <button
                onClick={handleSubmit}
                disabled={isSaving || !isFormValid}
                className="w-full py-4 bg-pp-orange text-white font-semibold rounded-[15px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('entryModal.saving')}
                  </>
                ) : (
                  t('entryModal.save')
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
